import React, { useState, useRef, useCallback } from 'react';
import { Button, Spinner, Icon } from '@/components/atoms';
import { validateFile, formatFileSize, type EntityType } from '@/schemas/attachments';
import { useUploadAttachmentMutation } from '@/hooks/useErpApi';

export interface FileUploadProps {
  entityType: EntityType;
  entityId: string;
  multiple?: boolean;
  accept?: string;
  maxSize?: number;
  onSuccess?: (attachments: unknown[]) => void;
  onError?: (error: string) => void;
}

interface UploadingFile {
  file: File;
  progress: number;
  error?: string;
  completed: boolean;
  cancelled: boolean;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  entityType,
  entityId,
  multiple = false,
  accept,
  onSuccess,
  onError,
}) => {
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mutateAsync: uploadFile } = useUploadAttachmentMutation();

  const handleFileValidation = useCallback(
    (files: FileList | null): File[] => {
      if (!files) return [];

      const validFiles: File[] = [];
      const errors: string[] = [];

      Array.from(files).forEach((file) => {
        const validation = validateFile(file);
        if (validation.valid) {
          validFiles.push(file);
        } else {
          errors.push(`${file.name}: ${validation.error}`);
        }
      });

      if (errors.length > 0) {
        onError?.(errors.join('; '));
      }

      return validFiles;
    },
    [onError]
  );

  const uploadSingleFile = useCallback(
    async (file: File, index: number) => {
      try {
        // Update progress to show uploading
        setUploadingFiles((prev) => {
          const updated = [...prev];
          updated[index] = { ...updated[index], progress: 10 };
          return updated;
        });

        const response = await uploadFile({
          entityType,
          entityId,
          file,
        });

        // Update progress to completed
        setUploadingFiles((prev) => {
          const updated = [...prev];
          updated[index] = { ...updated[index], progress: 100, completed: true };
          return updated;
        });

        return response;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Upload failed';
        setUploadingFiles((prev) => {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            error: errorMessage,
            completed: false,
          };
          return updated;
        });
        throw error;
      }
    },
    [uploadFile, entityType, entityId]
  );

  const handleFilesSelected = useCallback(
    async (files: FileList | null) => {
      const validFiles = handleFileValidation(files);
      if (validFiles.length === 0) return;

      // Initialize uploading state for all files
      const initialUploadingFiles: UploadingFile[] = validFiles.map((file) => ({
        file,
        progress: 0,
        completed: false,
        cancelled: false,
      }));

      setUploadingFiles(initialUploadingFiles);

      // Upload files
      const uploadedAttachments: unknown[] = [];
      for (let i = 0; i < validFiles.length; i++) {
        if (initialUploadingFiles[i].cancelled) continue;

        try {
          const attachment = await uploadSingleFile(validFiles[i], i);
          if (attachment) {
            uploadedAttachments.push(attachment);
          }
        } catch (error) {
          // Error already handled in uploadSingleFile
          console.error('Upload error:', error);
        }
      }

      // Call success callback with all uploaded attachments
      if (uploadedAttachments.length > 0) {
        onSuccess?.(uploadedAttachments);
      }
    },
    [handleFileValidation, uploadSingleFile, onSuccess]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const files = e.dataTransfer.files;
      handleFilesSelected(files);
    },
    [handleFilesSelected]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFilesSelected(e.target.files);
      // Reset input value to allow re-uploading same file
      e.target.value = '';
    },
    [handleFilesSelected]
  );

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleCancelFile = useCallback((index: number) => {
    setUploadingFiles((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], cancelled: true };
      return updated;
    });
  }, []);

  const handleClearCompleted = useCallback(() => {
    setUploadingFiles([]);
  }, []);

  const getAcceptedTypes = (): string => {
    if (accept) return accept;

    // Default accepted types based on entity type
    switch (entityType) {
      case 'logo':
        return 'image/*';
      case 'po':
      case 'grn':
        return '.pdf,.doc,.docx,.xls,.xlsx,.csv,image/*';
      default:
        return '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.md,image/*';
    }
  };

  const getAcceptedTypesLabel = (): string => {
    switch (entityType) {
      case 'logo':
        return 'Images (PNG, JPG, SVG)';
      case 'po':
      case 'grn':
        return 'PDF, Images, Excel, Word';
      default:
        return 'PDF, Images, Excel, Word, Text';
    }
  };

  const hasActiveUploads = uploadingFiles.some((f) => !f.completed && !f.cancelled && !f.error);

  return (
    <div className="space-y-4">
      {/* Upload Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          border-2 border-dashed rounded-lg p-8 text-center transition-colors
          ${
            isDragOver
              ? 'border-primary-500 bg-primary-50'
              : 'border-neutral-300 bg-neutral-50 hover:border-neutral-400'
          }
          ${hasActiveUploads ? 'opacity-50 pointer-events-none' : ''}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          accept={getAcceptedTypes()}
          onChange={handleFileInputChange}
          className="hidden"
          aria-label="File upload input"
        />

        <div className="flex flex-col items-center space-y-4">
          <div
            className={`
            w-16 h-16 rounded-full flex items-center justify-center
            ${isDragOver ? 'bg-primary-100' : 'bg-neutral-200'}
          `}
          >
            <Icon name="Upload" size="lg" className="text-neutral-600" />
          </div>

          <div className="space-y-2">
            <p className="text-lg font-medium text-neutral-900">Drag & drop files here</p>
            <p className="text-sm text-neutral-600">or</p>
            <Button
              type="button"
              variant="primary"
              onClick={handleBrowseClick}
              disabled={hasActiveUploads}
            >
              Browse Files
            </Button>
          </div>

          <div className="text-sm text-neutral-500 space-y-1">
            <p>Accepted: {getAcceptedTypesLabel()}</p>
            <p>Max size: 10MB per file</p>
          </div>
        </div>
      </div>

      {/* Uploading Files List */}
      {uploadingFiles.length > 0 && (
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-medium text-neutral-900">
              {hasActiveUploads ? 'Uploading...' : 'Upload Complete'}
            </h3>
            {!hasActiveUploads && (
              <Button type="button" variant="secondary" size="sm" onClick={handleClearCompleted}>
                Clear
              </Button>
            )}
          </div>

          <div className="space-y-2">
            {uploadingFiles.map((uploadingFile, index) => {
              if (uploadingFile.cancelled) return null;

              return (
                <div
                  key={`${uploadingFile.file.name}-${index}`}
                  className="flex items-center gap-3 p-3 bg-white border border-neutral-200 rounded-lg"
                >
                  <div className="flex-shrink-0">
                    {uploadingFile.completed ? (
                      <div className="w-10 h-10 rounded-full bg-success-100 flex items-center justify-center">
                        <Icon name="Check" size="sm" className="text-success-600" />
                      </div>
                    ) : uploadingFile.error ? (
                      <div className="w-10 h-10 rounded-full bg-error-100 flex items-center justify-center">
                        <Icon name="X" size="sm" className="text-error-600" />
                      </div>
                    ) : (
                      <Spinner size="sm" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-900 truncate">
                      {uploadingFile.file.name}
                    </p>
                    <p className="text-xs text-neutral-500">
                      {formatFileSize(uploadingFile.file.size)}
                    </p>

                    {uploadingFile.error && (
                      <p className="text-xs text-error-600 mt-1">{uploadingFile.error}</p>
                    )}

                    {!uploadingFile.completed && !uploadingFile.error && (
                      <div className="mt-2">
                        <div className="w-full bg-neutral-200 rounded-full h-1.5">
                          <div
                            className="bg-primary-600 h-1.5 rounded-full transition-all"
                            style={{ width: `${uploadingFile.progress}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {!uploadingFile.completed && !uploadingFile.error && (
                    <button
                      type="button"
                      onClick={() => handleCancelFile(index)}
                      className="flex-shrink-0 text-neutral-400 hover:text-neutral-600"
                      aria-label="Cancel upload"
                    >
                      <Icon name="X" size="sm" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
